import { LeadUiModel } from '../../shared/ui-models/lead.ui-model';

/**
 * The shape the form edits. Flat and all-strings, because that is what an
 * `<input>` holds — conversion to the API's numbers, arrays and booleans
 * happens once, at submit.
 */
export interface FormState {
  vertical: string;
  source: string;
  name: string;
  phone: string;
  email: string;
  message: string;
  marketingChannel: string;
  messagingChannel: string;
  adType: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  marketingConsent: boolean;
  consentSource: string;
  /**
   * The **stored** shape — numbers as numbers, add-ons as an array — not the
   * strings the inputs hold. The diff has to compare what would be written
   * against what is there, and `12` typed into a number field is not a change
   * from a stored `12`.
   */
  details: Record<string, unknown>;
}

const EMPTY_FORM: FormState = {
  vertical: '',
  source: 'admin',
  name: '',
  phone: '',
  email: '',
  message: '',
  marketingChannel: '',
  messagingChannel: '',
  adType: '',
  utmSource: '',
  utmMedium: '',
  utmCampaign: '',
  marketingConsent: false,
  consentSource: '',
  details: {}
};

/** A stored detail value as the form holds it. Arrays join, dates trim to a day. */
const detailToInput = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  // `<input type="date">` accepts only `yyyy-mm-dd`; an ISO timestamp makes it
  // render blank, which reads as "this lead has no check-in date".
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return value.slice(0, 10);
  return String(value);
};

/** The string map the detail widgets bind to. */
export function detailInputsFrom(lead?: LeadUiModel): Record<string, string> {
  const inputs: Record<string, string> = {};
  Object.keys(lead?.details ?? {}).forEach((key: string) => {
    inputs[key] = detailToInput((lead?.details as Record<string, unknown>)[key]);
  });
  return inputs;
}

/** Fills the form from an existing lead. Create mode gets `EMPTY_FORM`. */
export function formStateFrom(lead?: LeadUiModel): FormState {
  if (!lead) return EMPTY_FORM;

  return {
    vertical: lead.vertical ?? '',
    source: lead.source ?? '',
    name: lead.name ?? '',
    phone: lead.phone ?? '',
    email: lead.email ?? '',
    message: lead.message ?? '',
    marketingChannel: lead.marketingChannel ?? '',
    messagingChannel: lead.messagingChannel ?? '',
    adType: lead.adType ?? '',
    utmSource: lead.utmSource ?? '',
    utmMedium: lead.utmMedium ?? '',
    utmCampaign: lead.utmCampaign ?? '',
    marketingConsent: Boolean(lead.marketingConsent),
    consentSource: lead.consentSource ?? '',
    details: (lead.details as Record<string, unknown>) ?? {}
  };
}

/**
 * Key order is not a change.
 *
 * `JSON.stringify` on two objects with the same entries in a different order
 * gives different strings, and the order here is genuinely arbitrary: stored
 * details come back in Mongo's order while the form rebuilds them in the
 * vertical's declared key order. Comparing raw would mark every lead dirty the
 * moment its modal opened.
 */
function sameDetails(before: Record<string, unknown>, after: Record<string, unknown>): boolean {
  const keys = Object.keys(before).sort();
  const otherKeys = Object.keys(after).sort();
  if (keys.join('|') !== otherKeys.join('|')) return false;
  return keys.every((key: string) => JSON.stringify(before[key]) === JSON.stringify(after[key]));
}

/** How a form field maps onto the API's field name. */
const API_KEY: Record<string, string> = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign'
};

/**
 * Only what the operator actually changed.
 *
 * A full-object PUT is the obvious thing and the wrong one here. Every field
 * is optional upstream, the service writes a per-field diff into the lead's
 * timeline, and `details` is replaced wholesale — so sending everything would
 * fill the timeline with edits nobody made and could overwrite a detail key
 * this form never showed.
 */
export function changedFields(before: FormState, after: FormState): Record<string, unknown> {
  const changes: Record<string, unknown> = {};

  (Object.keys(after) as (keyof FormState)[]).forEach((key: keyof FormState) => {
    if (key === 'details') return;
    if (before[key] === after[key]) return;
    changes[API_KEY[key as string] ?? (key as string)] = after[key];
  });

  // `details` goes whole or not at all: the service replaces the sub-document
  // rather than merging into it, so a partial object would delete the keys it
  // left out.
  if (!sameDetails(before.details, after.details)) {
    changes.details = after.details;
  }

  return changes;
}
